import { makePersisted } from "@solid-primitives/storage";
import { type Navigator, useNavigate } from "@solidjs/router";
import BigNumber from "bignumber.js";
import { isKnownTokenAddress } from "boltz-swaps/evm";
import { decodeInvoice } from "boltz-swaps/invoice";
import {
    type Accessor,
    type JSX,
    type Setter,
    createContext,
    createEffect,
    createSignal,
    on,
    useContext,
} from "solid-js";

import { config } from "../config";
import {
    type AssetType,
    BTC,
    LBTC,
    LN,
    assets,
    isBitcoinOnlyPair,
} from "../consts/Assets";
import { type AssetSelection, Side, UrlParam } from "../consts/Enums";
import type { DictKey } from "../i18n/i18n";
import Pair, { RequiredInput } from "../utils/Pair";
import { validateAddress } from "../utils/compat";
import { isInvoice, isLnurl } from "../utils/invoice";
import { getUrlParam, resetUrlParam, urlParamIsSet } from "../utils/urlParams";
import { useGlobalContext } from "./Global";

const isValidForAsset = (asset: typeof BTC | typeof LBTC, address: string) => {
    try {
        return validateAddress(asset, address);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
        return false;
    }
};

const handleDestination: Partial<
    Record<
        AssetType,
        {
            isValid: (destination: string) => boolean;
            action: (
                destination: string,
                setters: {
                    setOnchainAddress: Setter<string>;
                    setInvoice: Setter<string>;
                    setAssetReceive: (asset: string) => void;
                    setAddressValid: Setter<boolean>;
                    setInvoiceValid: Setter<boolean>;
                },
            ) => ReturnType<typeof setDestination>;
        }
    >
> = {
    [BTC]: {
        isValid: (destination) => isValidForAsset(BTC, destination),
        action: (
            destination,
            { setOnchainAddress, setAssetReceive, setAddressValid },
        ) => {
            setOnchainAddress(destination);
            setAssetReceive(BTC);
            setAddressValid(true);
            return { destinationAsset: BTC, destination };
        },
    },
    [LBTC]: {
        isValid: (destination) => isValidForAsset(LBTC, destination),
        action: (
            destination,
            { setOnchainAddress, setAssetReceive, setAddressValid },
        ) => {
            setOnchainAddress(destination);
            setAssetReceive(LBTC);
            setAddressValid(true);
            return { destinationAsset: LBTC, destination };
        },
    },
    [LN]: {
        isValid: (destination) =>
            isInvoice(destination) || isLnurl(destination),
        action: (
            destination,
            { setAssetReceive, setInvoice, setInvoiceValid },
        ) => {
            setAssetReceive(LN);
            setInvoice(destination);
            setInvoiceValid(true);
            return { destinationAsset: LN, destination };
        },
    },
};

const setDestination = (
    setAssetReceive: (asset: string) => void,
    setInvoice: Setter<string>,
    setOnchainAddress: Setter<string>,
    setAddressValid: Setter<boolean>,
    setInvoiceValid: Setter<boolean>,
    receiveAsset: string,
): { destinationAsset?: string; destination?: string } => {
    const destination = getUrlParam("destination");
    if (urlParamIsSet(destination)) {
        for (const { isValid, action } of Object.values(handleDestination)) {
            if (isValid(destination)) {
                return action(destination, {
                    setOnchainAddress,
                    setAssetReceive,
                    setAddressValid,
                    setInvoice,
                    setInvoiceValid,
                });
            }
        }

        if (receiveAsset === BTC || receiveAsset === LBTC) {
            setOnchainAddress(destination);
            setAssetReceive(receiveAsset);
            setAddressValid(false);
            return { destinationAsset: receiveAsset, destination };
        }

        if (receiveAsset === LN) {
            setAssetReceive(LN);
            setInvoice(destination);
            return { destinationAsset: LN, destination };
        }
    }

    return { destinationAsset: undefined, destination: undefined };
};

const isValidAsset = (asset: string) =>
    urlParamIsSet(asset) && assets.includes(asset);

const parseAmount = (amount: string): BigNumber | undefined => {
    if (!urlParamIsSet(amount)) {
        return undefined;
    }

    const parsedAmount = new BigNumber(amount);
    if (parsedAmount.isNaN()) {
        return undefined;
    }
    return parsedAmount;
};

const handleUrlParams = (
    pair: Accessor<Pair>,
    setPair: Setter<Pair>,
    setInvoice: Setter<string>,
    setOnchainAddress: Setter<string>,
    setAmountChanged: Setter<Side>,
    setSendAmount: Setter<BigNumber>,
    setReceiveAmount: Setter<BigNumber>,
    setAddressValid: Setter<boolean>,
    setInvoiceValid: Setter<boolean>,
    setDestinationLocked: Setter<boolean>,
    navigate: Navigator,
) => {
    const setAssetReceive = (asset: string) => {
        setPair(new Pair(pair().pairs, pair().fromAsset, asset));
    };

    const sendAsset = getUrlParam(UrlParam.SendAsset);
    const receiveAsset = getUrlParam(UrlParam.ReceiveAsset);
    const { destinationAsset, destination } = setDestination(
        setAssetReceive,
        setInvoice,
        setOnchainAddress,
        setAddressValid,
        setInvoiceValid,
        receiveAsset,
    );

    // Build the final from/to directly so we don't auto-resolve conflicts;
    // the URL may intentionally specify an invalid same-asset pair
    let fromAsset = pair().fromAsset;
    let toAsset = pair().toAsset;

    if (isValidAsset(sendAsset)) {
        fromAsset = sendAsset;
    }

    // The type of the destination takes precedence
    if (destinationAsset !== undefined) {
        toAsset = destinationAsset;
    } else if (isValidAsset(receiveAsset)) {
        toAsset = receiveAsset;
    }

    if (fromAsset !== pair().fromAsset || toAsset !== pair().toAsset) {
        setPair(new Pair(pair().pairs, fromAsset, toAsset));
    }

    // Lightning invoice amounts take precedence unless this is a LN addr or bolt12 offer
    if (
        destinationAsset !== LN ||
        destination === undefined ||
        !isInvoice(destination)
    ) {
        const sendAmount = parseAmount(getUrlParam(UrlParam.SendAmount));
        if (sendAmount) {
            setAmountChanged(Side.Send);
            setSendAmount(sendAmount);
        }

        if (sendAmount === undefined) {
            const receiveAmount = parseAmount(
                getUrlParam(UrlParam.ReceiveAmount),
            );

            if (receiveAmount) {
                setAmountChanged(Side.Receive);
                setReceiveAmount(BigNumber(receiveAmount));
            }
        }
    } else {
        try {
            const sats = decodeInvoice(destination).satoshis;
            if (sats > 0) {
                setAmountChanged(Side.Receive);
                setReceiveAmount(BigNumber(sats));
                if (getUrlParam(UrlParam.LockOutput) === "true") {
                    setDestinationLocked(true);
                }
            }
        } catch {
            // Invalid invoice; leave amount unse.
        }
    }

    const params = [
        UrlParam.Destination,
        UrlParam.SendAsset,
        UrlParam.ReceiveAsset,
        UrlParam.SendAmount,
        UrlParam.ReceiveAmount,
        UrlParam.Embedded,
        UrlParam.Theme,
        UrlParam.LockOutput,
        UrlParam.ParentOrigin,
    ];

    if (
        config.isPro &&
        params.some((param) => urlParamIsSet(getUrlParam(param)))
    ) {
        navigate("/swap");
    }

    params.forEach((param) => {
        if (urlParamIsSet(getUrlParam(param))) {
            resetUrlParam(param);
        }
    });
};

export type CreateContextType = {
    pair: Accessor<Pair>;
    setPair: Setter<Pair>;
    getGasToken: Accessor<boolean | undefined>;
    setGetGasToken: Setter<boolean | undefined>;
    invoice: Accessor<string>;
    setInvoice: Setter<string>;
    lnurl: Accessor<string>;
    setLnurl: Setter<string>;
    bolt12Offer: Accessor<string | undefined>;
    setBolt12Offer: Setter<string | undefined>;
    onchainAddress: Accessor<string>;
    setOnchainAddress: Setter<string>;
    assetSelection: Accessor<AssetSelection | null>;
    setAssetSelection: Setter<AssetSelection | null>;
    assetSelected: Accessor<string>;
    setAssetSelected: Setter<string>;
    networkSelectCanonical: Accessor<string | undefined>;
    setNetworkSelectCanonical: Setter<string | undefined>;
    valid: Accessor<boolean>;
    setValid: Setter<boolean>;
    invoiceValid: Accessor<boolean>;
    setInvoiceValid: Setter<boolean>;
    addressValid: Accessor<boolean>;
    setAddressValid: Setter<boolean>;
    amountValid: Accessor<boolean>;
    setAmountValid: Setter<boolean>;
    sendAmount: Accessor<BigNumber>;
    setSendAmount: Setter<BigNumber>;
    receiveAmount: Accessor<BigNumber>;
    setReceiveAmount: Setter<BigNumber>;
    sendAmountFormatted: Accessor<string>;
    setSendAmountFormatted: Setter<string>;
    receiveAmountFormatted: Accessor<string>;
    setReceiveAmountFormatted: Setter<string>;
    resetAmounts: () => void;
    amountChanged: Accessor<Side>;
    setAmountChanged: Setter<Side>;
    minimum: Accessor<number>;
    setMinimum: Setter<number>;
    maximum: Accessor<number>;
    setMaximum: Setter<number>;
    limitsLoading: Accessor<boolean>;
    setLimitsLoading: Setter<boolean>;
    boltzFee: Accessor<number>;
    setBoltzFee: Setter<number>;
    minerFee: Accessor<number>;
    setMinerFee: Setter<number>;
    setInvoiceError: Setter<DictKey | undefined>;
    invoiceError: Accessor<DictKey | undefined>;
    bolt12Loading: Accessor<boolean>;
    setBolt12Loading: Setter<boolean>;
    quoteLoading: Accessor<boolean>;
    setQuoteLoading: Setter<boolean>;
    quoteError: Accessor<DictKey | undefined>;
    setQuoteError: Setter<DictKey | undefined>;
    destinationLocked: Accessor<boolean>;
    setDestinationLocked: Setter<boolean>;
};

const CreateContext = createContext<CreateContextType>();

const CreateProvider = (props: { children: JSX.Element }) => {
    const navigate = useNavigate();
    const { pairs, regularPairs, bitcoinOnly } = useGlobalContext();

    const [invoice, setInvoice] = createSignal<string>("");
    const [lnurl, setLnurl] = createSignal("");
    const [bolt12Offer, setBolt12Offer] = createSignal<string | undefined>(
        undefined,
    );
    const [onchainAddress, setOnchainAddress] = createSignal("");

    // eslint-disable-next-line solid/reactivity
    const [assetFrom, setAssetFrom] = makePersisted(createSignal(LN), {
        name: "assetSend",
    });
    const [assetTo, setAssetTo] = makePersisted(
        // eslint-disable-next-line solid/reactivity
        createSignal(BTC),
        { name: "assetReceive" },
    );

    const [pair, setPair] = createSignal<Pair>(
        new Pair(undefined, assetFrom(), assetTo()),
    );
    const [getGasToken, setGetGasToken] = createSignal<boolean | undefined>(
        undefined,
    );

    // asset selection
    const [assetSelection, setAssetSelection] =
        createSignal<AssetSelection | null>(null);
    const [assetSelected, setAssetSelected] = createSignal<string>("");
    const [networkSelectCanonical, setNetworkSelectCanonical] = createSignal<
        string | undefined
    >(undefined);

    // validation
    const [valid, setValid] = createSignal(false);
    const [invoiceValid, setInvoiceValid] = createSignal(false);
    const [addressValid, setAddressValid] = createSignal(false);
    const [amountValid, setAmountValid] = createSignal(false);
    const [invoiceError, setInvoiceError] = createSignal<DictKey | undefined>(
        undefined,
    );
    const [bolt12Loading, setBolt12Loading] = createSignal(false);
    const [quoteLoading, setQuoteLoading] = createSignal(false);
    const [quoteError, setQuoteError] = createSignal<DictKey | undefined>(
        undefined,
    );
    const [destinationLocked, setDestinationLocked] = createSignal(false);

    const resetDestinationState = () => {
        if (destinationLocked()) {
            return;
        }
        setInvoice("");
        setInvoiceValid(false);
        setInvoiceError(undefined);
        setLnurl("");
        setBolt12Offer(undefined);
        setOnchainAddress("");
        setAddressValid(false);
    };

    const normalizeBitcoinOnlyPair = (current: Pair) => {
        if (isBitcoinOnlyPair(current.fromAsset, current.toAsset)) {
            return current;
        }

        return new Pair(pairs(), LN, BTC, regularPairs());
    };

    const hasBlockedOnchainAddress = () =>
        isKnownTokenAddress(pair().toAsset, onchainAddress());

    createEffect(() => {
        if (amountValid() && pair().isRoutable) {
            const requiredInput = pair().requiredInput;
            const validDestinationAddress =
                addressValid() && !hasBlockedOnchainAddress();
            if (
                ((requiredInput === RequiredInput.Address ||
                    requiredInput === RequiredInput.Web3) &&
                    validDestinationAddress) ||
                (requiredInput === RequiredInput.Invoice && invoiceValid())
            ) {
                setValid(true);
                return;
            }
        }
        setValid(false);
    });

    createEffect(() => {
        if (addressValid() && hasBlockedOnchainAddress()) {
            setAddressValid(false);
        }
    });

    createEffect(
        on([pairs, regularPairs], () => {
            setPair(
                new Pair(
                    pairs(),
                    pair().fromAsset,
                    pair().toAsset,
                    regularPairs(),
                ),
            );
        }),
    );

    createEffect(() => {
        const latest = pair();
        setAssetFrom(latest.fromAsset);
        setAssetTo(latest.toAsset);
    });

    createEffect(() => {
        if (!bitcoinOnly()) return;

        const latest = pair();
        const normalized = normalizeBitcoinOnlyPair(latest);
        if (
            latest.fromAsset === normalized.fromAsset &&
            latest.toAsset === normalized.toAsset
        ) {
            return;
        }

        if (latest.toAsset !== normalized.toAsset) {
            resetDestinationState();
        }

        setPair(normalized);
    });

    // amounts
    const [sendAmount, setSendAmount] = createSignal(BigNumber(0));
    const [receiveAmount, setReceiveAmount] = createSignal(BigNumber(0));
    const [sendAmountFormatted, setSendAmountFormatted] = createSignal("0");
    const [receiveAmountFormatted, setReceiveAmountFormatted] =
        createSignal("0");
    const resetAmounts = () => {
        setSendAmount(BigNumber(0));
        setReceiveAmount(BigNumber(0));
        setSendAmountFormatted("");
        setReceiveAmountFormatted("");
        setAmountValid(false);
        setQuoteError(undefined);
    };

    const [amountChanged, setAmountChanged] = createSignal(Side.Send);
    const [minimum, setMinimum] = createSignal<number>(0);
    const [maximum, setMaximum] = createSignal<number>(0);
    const [limitsLoading, setLimitsLoading] = createSignal<boolean>(true);

    // fees
    const [boltzFee, setBoltzFee] = createSignal(0);
    const [minerFee, setMinerFee] = createSignal(0);

    // eslint-disable-next-line solid/reactivity
    handleUrlParams(
        pair,
        setPair,
        setInvoice,
        setOnchainAddress,
        setAmountChanged,
        setSendAmount,
        setReceiveAmount,
        setAddressValid,
        setInvoiceValid,
        setDestinationLocked,
        navigate,
    );

    return (
        <CreateContext.Provider
            value={{
                pair,
                setPair,
                getGasToken,
                setGetGasToken,
                invoice,
                setInvoice,
                lnurl,
                setLnurl,
                bolt12Offer,
                setBolt12Offer,
                onchainAddress,
                setOnchainAddress,
                assetSelection,
                setAssetSelection,
                assetSelected,
                setAssetSelected,
                networkSelectCanonical,
                setNetworkSelectCanonical,
                valid,
                setValid,
                invoiceValid,
                setInvoiceValid,
                addressValid,
                setAddressValid,
                amountValid,
                setAmountValid,
                sendAmount,
                setSendAmount,
                receiveAmount,
                setReceiveAmount,
                sendAmountFormatted,
                setSendAmountFormatted,
                receiveAmountFormatted,
                setReceiveAmountFormatted,
                resetAmounts,
                amountChanged,
                setAmountChanged,
                minimum,
                setMinimum,
                maximum,
                setMaximum,
                limitsLoading,
                setLimitsLoading,
                boltzFee,
                setBoltzFee,
                minerFee,
                setMinerFee,
                invoiceError,
                setInvoiceError,
                bolt12Loading,
                setBolt12Loading,
                quoteLoading,
                setQuoteLoading,
                quoteError,
                setQuoteError,
                destinationLocked,
                setDestinationLocked,
            }}>
            {props.children}
        </CreateContext.Provider>
    );
};

const useCreateContext = () => {
    const context = useContext(CreateContext);
    if (!context) {
        throw new Error("useCreateContext: cannot find a CreateContext");
    }
    return context;
};

export { useCreateContext, CreateProvider };
